import React, { ReactNode, useMemo } from "react";
import { useDispatch } from "react-redux";
import { openOpInfo } from "../../components/Popup/PopupOpInfo";
import { createKeyActivityLog } from "../../pages/PlanOperationPage/Plan/Calendar/Operations/actions";
import { planOpActions } from "../../pages/PlanOperationPage/reducer";
import { getActivityLogDurationEst, getFactorySiteTime, getLastPlaceByTime } from "../../pages/PlanOperationPage/util";
import { resolveActivityLog, updateTime } from "../../pages/PlanOperationPage/util/function_operation/global";
import { OpService } from "../../util/services/operation";
import { AppState, getStore } from "../../util/store/store";
import { DELIVERY_TYPE, OP_STATUS, OPERATION_TYPE, VESSEL_TYPE } from "../../util/varibles/constants";
import { getOperationStatus, showErrorResponse } from "../../util/varibles/global";
import { IActivityLog, IAfterProcesses, ILevel, IOperation, IOperations, IVessel, TOperation } from "../../util/varibles/interface";
import { notify, NotifyCode } from "../../util/varibles/message";
import { createFastContext } from "../createFastContext2";

interface IState {
    modeDrag: string
    vessels: IVessel[] // For drag operation

    rootKey: string
    currentKey: string
    activityLogs: any

    isTimeline: boolean

    loading: boolean
    loadingKeys: Record<string, boolean>

    id: string
    left?: number
    top?: number
    vesselIndex: number
    vesselId: string
    vesselWidth?: number

    data?: IOperation
    level: ILevel

    width?: number

    targetX?: number
    targetY?: number
    maxWidth?: number
    maxScrollLeft?: number
    scrollHeight?: number
    isOwn?: boolean
    isDrag?: boolean
    canEditTimeline: boolean
    pasue: number
    realStatus?: OP_STATUS
    rootRef?: () => HTMLDivElement
    updateOp?: (value: Partial<IOperation>) => void
    getVessels: (state: AppState) => IVessel[]
    getOps: (state: AppState) => IOperations
}

const {
    Provider,
    useStore: useOperation,
    setStore: setOperation,
    getStore: getOperation,
    callEvent: callEventOperation
} = createFastContext<IState, {
    openInfo: () => void,
    resetState: (props: { isTimeline?: boolean, modeDrag?: string }) => void,
    getActivityLog: (props: {
        time: number,
        vessel: IVessel,
        currentKey?: string,
        abort?: AbortController,
        success?: (ops: IOperations) => void
    }) => void
}>({
    modeDrag: '',
    vessels: [],
    vesselIndex: 0,

    rootKey: '',
    currentKey: '',
    activityLogs: {},
    isTimeline: false,
    loading: false,
    loadingKeys: {},
    id: '',
    vesselId: '',
    level: {
        level: 0,
        ids: [],
        main: false,
        sub: 0,
        operation_code: ''
    },
    data: {
        activity_log: [],
        operation: {} as TOperation,
        vessel: {} as IVessel,
    },
    canEditTimeline: true,
    pasue: -1,
    getVessels: () => [],
    getOps: () => ({}),
});

const OperationProvider = React.memo(({ children, vesselIndex, ...args }: {
    children: ReactNode,
} & Partial<IState>) => {
    const { data } = args;
    const dispatch = useDispatch();

    const { currentKey, activityLogs, realStatus } = useMemo(() => {
        if (!data)
            return { currentKey: '-', activityLogs: { '-': [] }, realStatus: undefined }
        const currentKey = createKeyActivityLog(data.operation, data.vessel || { id: data.operation.vessel_id });
        return {
            rootKey: currentKey,
            currentKey,
            activityLogs: { [currentKey]: data.activity_log },
            realStatus: getOperationStatus(data.operation.status, data.operation.pauses)
        }
    }, [data]);

    return <Provider
        props={{ ...args, vesselIndex, rootKey: currentKey, currentKey, activityLogs, realStatus }}
        event={{
            openInfo: ({ get }) => {
                const { isOwn, getOps } = get();
                if (data) {
                    const { related_id: mainId = "" } = data.operation;
                    let spOps: IOperation[] = [];
                    if (mainId.length > 0) {
                        const ops = getOps(getStore());
                        spOps = Object.keys(ops).reduce((rs: IOperation[], key) => {
                            const { operation_type, related_id } = ops[key].operation;
                            if (operation_type === OPERATION_TYPE.SUPPORT && related_id === mainId) {
                                return [...rs, ops[key]]
                            }
                            return rs;
                        }, [])
                    }
                    openOpInfo({ isOwn, data, spOps })
                }
            },
            resetState: ({ isTimeline, modeDrag = '' } = {}, { set }) => {

                if (!data)
                    return;

                const currentKey = createKeyActivityLog(data.operation, data.vessel || { id: data.operation.vessel_id });

                set(prev => {
                    const state: Partial<IState> = {
                        loading: false,
                        loadingKeys: {},
                        modeDrag,
                        vesselIndex,
                        left: undefined,
                        top: undefined,
                        vesselId: data.vessel?.id || data.operation.vessel_id,
                        rootKey: currentKey,
                        currentKey,
                        activityLogs: { [currentKey]: data.activity_log },
                        isTimeline: prev.isTimeline,
                    }
                    if (isTimeline !== undefined)
                        state.isTimeline = isTimeline;

                    return state
                });
            },
            getActivityLog: ({ time, vessel, currentKey = '', abort, success }, { get, set, event }) => {
                const { data, activityLogs, loadingKeys } = get();
                if (!data)
                    return;

                const requestKey = currentKey || createKeyActivityLog(data.operation, vessel);

                if (!success && (activityLogs[requestKey] || loadingKeys[requestKey])) {
                    return;
                }

                set(prev => ({
                    loading: true,
                    currentKey: requestKey,
                    loadingKeys: {
                        ...prev.loadingKeys,
                        [requestKey]: true
                    }
                }));

                const { id, operation_type } = data.operation;
                const isSameVessel = vessel.id === (data.vessel?.id || data.operation.vessel_id);
                const finishLoading = () => {
                    set(prev => {
                        const nextLoadingKeys = {
                            ...prev.loadingKeys,
                            [requestKey]: false
                        };

                        return {
                            loading: Object.values(nextLoadingKeys).some(Boolean),
                            loadingKeys: nextLoadingKeys
                        };
                    });
                };
                const failureDefault = (error: any) => {
                    finishLoading();
                    if (success) {
                        const { resetState } = event();
                        resetState({});
                        showErrorResponse('Get activity logs failed', error);
                    }
                }
                const successDefault = (activity_log: IActivityLog[]) => {
                    set((prev) => {
                        const nextLoadingKeys = {
                            ...prev.loadingKeys,
                            [requestKey]: false
                        };
                        const latestStartTime = prev.currentKey === requestKey
                            ? prev.activityLogs[prev.rootKey]?.[0]?.est_start_time ?? time
                            : time;

                        return {
                            loading: Object.values(nextLoadingKeys).some(Boolean),
                            loadingKeys: nextLoadingKeys,
                            rootKey: prev.currentKey === requestKey ? requestKey : prev.rootKey,
                            activityLogs: {
                                ...prev.activityLogs,
                                [requestKey]: updateTime(activity_log, 0, latestStartTime)
                            }
                        }
                    })
                }
                if (operation_type === OPERATION_TYPE.HARVEST) {
                    const { id: vessel_id, type: vesselType } = vessel;
                    const { sub_operations } = data.operation;
                    const isOnlyDirect = sub_operations.some(item => !(item.delivery_types || []).some(sub => sub.type !== DELIVERY_TYPE.DIRECT))
                    if (vesselType === VESSEL_TYPE.PROCESS_VESSEL && !isOnlyDirect) {
                        finishLoading();
                        if (success) {
                            const { resetState } = event();
                            resetState({});
                            notify.error(NotifyCode.E43)([vessel.name]);
                        }
                    } else {
                        const { site_id, factory_id } = data.operation;
                        const { start_place_id, start_place_field } = getLastPlaceByTime(vessel_id, time, [id]);
                        const {
                            factory_time,
                            factoryTime,
                            site_arrival_time,
                            priority
                        } = getFactorySiteTime(site_id, factory_id, vesselType, time);
                        const params = {
                            start_time: time,
                            factory_time,
                            site_arrival_time,
                            packing_time: factoryTime,
                            priority,
                            start_place_id,
                            start_place_field,
                            source_id: data.operation.site_id,
                            destination_id: data.operation.factory_id,
                            vessel_id,
                            is_journey_to_site: data.operation.is_journey_to_site,
                            is_cleaning: data.operation.is_cleaning,
                            sub_operations: sub_operations.map(item => {
                                const { delivery_types = [], ...subArgs } = item;

                                return {
                                    ...subArgs,
                                    delivery_types: delivery_types.map(sub => {
                                        const { tanks, ...deliveryTypeArgs } = sub;

                                        return { ...deliveryTypeArgs, tanks: [] }
                                    })
                                }
                            })
                        }

                        const baseline = isSameVessel
                            ? getActivityLogDurationEst(data, abort)
                            : Promise.resolve(undefined);
                        baseline
                            .then(async durationEst => {
                                const rs = await OpService.harvestTanks(params, abort);
                                const { activity_log, sub_operations: newSubs, tanks, router_id = '-' } = rs;
                                const resolved = resolveActivityLog(isSameVessel ? data.activity_log : [], activity_log, durationEst);
                                if (success) {
                                    finishLoading();
                                    success({
                                        [data.operation.id]: {
                                            ...data,
                                            operation: {
                                                ...data.operation, sub_operations: newSubs, tanks, router_id,
                                                duration_est: resolved.duration_est
                                            },
                                            activity_log: resolved.activity_log,
                                            vessel
                                        }
                                    })
                                } else
                                    successDefault(resolved.activity_log)
                            })
                            .catch(error => {
                                failureDefault(error);
                            })
                    }

                } else if (operation_type === OPERATION_TYPE.TREATMENT) {
                    const { round_map = {} } = data.operation;
                    dispatch(planOpActions.getActivityLog({
                        source: [{
                            activity_log: isSameVessel ? data.activity_log : [],
                            operation: {
                                ...data.operation,
                                round_map: {}
                            },
                            vessel
                        }],
                        showError: false,
                        skipDurationEst: !isSameVessel,
                        properties: {
                            [id]: {
                                start: time,
                                after_round: Object.keys(round_map).reduce((rs: Record<string, IAfterProcesses[]>, key) => {
                                    const { after_processes = [] } = round_map[key];
                                    if (after_processes.length > 0)
                                        rs[key] = after_processes;
                                    return rs;
                                }, {})
                            }
                        },
                        signal: abort,
                        success: success ? (ops) => {
                            finishLoading();
                            success(ops);
                        } : (ops) => successDefault(ops[id].activity_log),
                        failure: failureDefault
                    }))
                } else {
                    dispatch(planOpActions.getActivityLog({
                        source: [{ operation: data.operation, vessel }],
                        showError: false,
                        properties: { [id]: { start: time } },
                        signal: abort,
                        success: success ? (ops) => {
                            finishLoading();
                            success(ops);
                        } : (ops) => successDefault(ops[id].activity_log),
                        failure: failureDefault
                    }))
                }
            }
        }}
    >
        {children}
    </Provider>
}, (prev, next) =>
    JSON.stringify(prev.data) === JSON.stringify(next.data)
    && prev.level === next.level
    && prev.width === next.width
    && prev.vesselIndex === next.vesselIndex)


export { callEventOperation, getOperation, OperationProvider, setOperation, useOperation };
